import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext.js';
import { LoadingState } from '@/components/common/LoadingState.js';
import { EmptyState } from '@/components/common/EmptyState.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Brain, Pin, Tag, Trash2, Filter } from 'lucide-react';
import { cn, formatDate } from '@/lib/utils.js';
import { AddMemoryModal } from './AddMemoryModal.js';
import { ViewMemoryModal } from './ViewMemoryModal.js';
import type { Memory } from '@/types/api.js';

interface MemoriesGridViewProps {
  searchQuery: string;
  showAddModal: boolean;
  onCloseAddModal: () => void;
}

export function MemoriesGridView({ searchQuery, showAddModal, onCloseAddModal }: MemoriesGridViewProps) {
  const { api } = useAuth();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null);

  useEffect(() => {
    loadMemories();
  }, []);

  const loadMemories = async () => {
    try {
      setLoading(true);
      const data = await api.getMemories({ limit: 100 });
      setMemories(data.sort((a, b) => b.updatedAt - a.updatedAt));
    } catch (error) {
      console.error('Failed to load memories:', error);
      setMemories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.deleteMemory(id);
      setMemories((prev) => prev.filter((m) => m.id !== id));
    } catch (error) {
      console.error('Failed to delete memory:', error);
    }
  };

  const handleTogglePin = async (memory: Memory, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const updated = await api.updateMemory(memory.id, {
        pinned: !memory.metadata?.pinned,
      });
      setMemories((prev) =>
        prev.map((m) => (m.id === memory.id ? updated : m))
      );
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  // Get all unique tags
  const allTags = Array.from(
    new Set(memories.flatMap((m) => m.tags || []))
  ).sort();

  // Filter memories
  const filteredMemories = memories.filter((memory) => {
    const matchesSearch = memory.content
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.some((tag) => memory.tags?.includes(tag));
    return matchesSearch && matchesTags;
  });

  const pinnedMemories = filteredMemories.filter((m) => m.metadata?.pinned);
  const unpinnedMemories = filteredMemories.filter((m) => !m.metadata?.pinned);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  if (loading) {
    return <LoadingState message="Loading memories..." />;
  }

  return (
    <>
      {/* Modals */}
      <AddMemoryModal
        open={showAddModal}
        onClose={onCloseAddModal}
        onSuccess={loadMemories}
      />
      <ViewMemoryModal
        memory={selectedMemory}
        onClose={() => setSelectedMemory(null)}
        onUpdate={loadMemories}
        onDelete={loadMemories}
      />

      <div className="flex flex-col h-full bg-background">
      {/* Tag Filter Bar */}
      {allTags.length > 0 && (
        <div className="px-6 py-3 border-b border-border/50 bg-muted/20">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="text-sm text-muted-foreground flex-shrink-0">Tags:</span>
            {allTags.map((tag) => (
              <Badge
                key={tag}
                variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                className={cn(
                  'cursor-pointer transition-colors',
                  selectedTags.includes(tag) && 'bg-primary text-primary-foreground'
                )}
                onClick={() => toggleTag(tag)}
              >
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </Badge>
            ))}
            {selectedTags.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => setSelectedTags([])}
              >
                Clear filters
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Grid Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {memories.length === 0 ? (
          <EmptyState
            icon={Brain}
            title="No memories yet"
            description="Click 'Add Memory' above to start building your knowledge base."
          />
        ) : filteredMemories.length === 0 ? (
          <EmptyState
            icon={Brain}
            title="No matches found"
            description="Try different search terms or tags"
          />
        ) : (
          <div className="space-y-6">
            {/* Pinned Memories */}
            {pinnedMemories.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Pin className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                    Pinned
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {pinnedMemories.map((memory) => (
                    <MemoryCard
                      key={memory.id}
                      memory={memory}
                      onDelete={handleDelete}
                      onTogglePin={handleTogglePin}
                      onClick={() => setSelectedMemory(memory)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Memories */}
            {unpinnedMemories.length > 0 && (
              <div>
                {pinnedMemories.length > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="h-4 w-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                      All Memories
                    </h3>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {unpinnedMemories.map((memory) => (
                    <MemoryCard
                      key={memory.id}
                      memory={memory}
                      onDelete={handleDelete}
                      onTogglePin={handleTogglePin}
                      onClick={() => setSelectedMemory(memory)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

interface MemoryCardProps {
  memory: Memory;
  onDelete: (id: string, e: React.MouseEvent) => void;
  onTogglePin: (memory: Memory, e: React.MouseEvent) => void;
  onClick?: () => void;
}

function MemoryCard({ memory, onDelete, onTogglePin, onClick }: MemoryCardProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'group relative p-4 rounded-lg cursor-pointer transition-all',
        'border border-border/50 bg-card',
        'hover:border-primary/50 hover:shadow-lg',
        memory.metadata?.pinned && 'border-primary/30 bg-primary/5'
      )}
    >
      {/* Actions */}
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 bg-background/80 backdrop-blur-sm"
          onClick={(e) => onTogglePin(memory, e)}
        >
          <Pin
            className={cn(
              'h-3.5 w-3.5',
              memory.metadata?.pinned && 'fill-primary text-primary'
            )}
          />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 bg-background/80 backdrop-blur-sm"
          onClick={(e) => onDelete(memory.id, e)}
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </div>

      {/* Content */}
      <div className="space-y-3">
        <p className="text-sm text-foreground leading-relaxed pr-16">
          {memory.content}
        </p>

        {/* Tags */}
        {memory.tags && memory.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {memory.tags.map((tag, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="text-xs px-2 py-0.5"
              >
                <Tag className="h-2.5 w-2.5 mr-1" />
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-border/50">
          <p className="text-xs text-muted-foreground">
            {formatDate(memory.updatedAt)}
          </p>
          {memory.metadata?.source === 'system' && (
            <Badge variant="outline" className="text-xs">
              System
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}
